"use client";

import { type Slot } from "@/components/calendar/slot-visit/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/radix/tooltip";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { VisitSchedulingOverlay } from "@/components/visit-scheduling/visit-scheduling-overlay";
import { useAuth } from "@/hooks/use-auth";
import { useBuyerActions } from "@/hooks/use-buyer-actions";
import { BuyerRequestContext } from "@/hooks/use-buyer-request-data";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { banks, loanStatuses } from "@/info/bank-info";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import { useBuyerHelpers } from "@/utils/buyer/helpers";
import currencify from "@/utils/currency/format";
import { isSame } from "@/utils/date/is-same";
import { formatRange } from "@/utils/format-range";
import { getAge } from "@/utils/get-age";
import { getNameFromCode } from "@/utils/get-name-from-code";
import { logger } from "@/utils/logger";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { Info } from "lucide-react";
import { useCallback, useContext, useState } from "react";
import { toast } from "sonner";
interface PropertyDetailsProps {
  supOne: SellerUnitsAndProjectsOne;
  isLoading?: boolean;
}
export function PropertyDetails({
  supOne,
  isLoading = false
}: PropertyDetailsProps) {
  // Authentication and visit scheduling state
  const {
    isAuthenticated,
    user,
    refetchUser
  } = useAuth();
  const {
    openDialog
  } = useLoginDialog();
  const {
    handleScheduleVisit
  } = useBuyerActions();
  const {
    getBuyerGroupId: getBuyerGroupIdHelper,
    createBuyerGroup
  } = useBuyerHelpers(user, refetchUser);
  const [isVisitSchedulingOpen, setIsVisitSchedulingOpen] = useState(false);
  const {
    data,
    refetch,
    actions,
    setActions
  } = useContext(BuyerRequestContext) || {};
  const constructedOn = formatRange({
    data: (supOne.constructedOns || []).filter(Boolean).map(d => d.valueOf()),
    formatter: value => getAge({
      constructedOn: new Date(value)
    })
  });
  const pdValues = supOne?._id ? data?.buyerUnits?.find(({
    unit: u
  }) => u._id === supOne._id) : undefined;
  const isVisitRequested = user && actions?.requestVisitUnits.includes(supOne?._id ?? "") || pdValues?.processDetails?.[2]?.status === "requested";

  // Visit scheduling handler - similar to customer care widget
  const hRequestVisit = useCallback(async (slots: Slot[]) => {
    const propertyId = supOne._id;
    if (!propertyId) return;
    let buyerGroupId = getBuyerGroupIdHelper();
    if (!buyerGroupId) {
      buyerGroupId = await createBuyerGroup();
    }
    if (!buyerGroupId) return;
    try {
      // Create property object based on type
      const property = {
        __typename: "BuyerUnit",
        id: supOne._id,
        unit: {
          id: supOne._id
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      // Format slots for the mutation
      const formattedSlots = slots.map(({
        start,
        end
      }) => ({
        start: typeof start === "string" ? start : start.toISOString(),
        end: typeof end === "string" ? end : end.toISOString()
      }));

      // Use centralized schedule visit
      await handleScheduleVisit(property, formattedSlots, "property_detail");
      // Legacy context update for backward compatibility
      try {
        refetch && refetch();
      } catch (error) {
        logger.error("Failed to refetch buyer data", error);
      }
      if (setActions) {
        setActions({
          shortlistUnits: actions?.shortlistUnits || [],
          requestVisitUnits: supOne._id ? [...(actions?.requestVisitUnits || []), supOne._id ?? ""] : actions?.requestVisitUnits || [],
          shortlistProjects: actions?.shortlistProjects || [],
          requestVisitProjects: actions?.requestVisitProjects || []
        });
      }
      return true;
    } catch (e) {
      logger.error(e);
      toast.error("Could not request visit", {
        description: "Please try again later",
        duration: 5000,
        position: "bottom-right"
      });
    }
  }, [supOne._id, getBuyerGroupIdHelper, createBuyerGroup, handleScheduleVisit, setActions, refetch, actions?.shortlistUnits, actions?.requestVisitUnits, actions?.shortlistProjects, actions?.requestVisitProjects]);

  // Wrapper for Visit Scheduling Overlay
  const handleScheduleComplete = useCallback(async (slots_0: {
    start: string;
    end: string;
  }[]) => {
    const convertedSlots: Slot[] = slots_0.map(slot => ({
      start: new Date(slot.start),
      end: new Date(slot.end)
    }));
    await hRequestVisit(convertedSlots);
  }, [hRequestVisit]);

  // Check if property is a plot
  const isPlotProperty = supOne.propertyTypes?.some(type => type.toLowerCase() === "plot");
  // Property-specific details
  const propertyRows = [{
    label: "Builtup Area",
    value: formatRange({
      data: supOne.builtUpAreas,
      formatter: value_0 => value_0.toLocaleString()
    }),
    suffix: " sqft"
  }, {
    label: "Plot Area",
    value: formatRange({
      data: supOne.plotAreas,
      formatter: value_1 => value_1.toLocaleString()
    }),
    suffix: " sqft"
  },
  // Conditionally add Carpet Area for non-plot properties
  ...(isPlotProperty ? [] : [{
    label: "Carpet Area",
    value: "60-90%",
    info: <div className="space-y-2">
								<p className="font-semibold text-sm border-b pb-1">Note on Carpet Area</p>
								<p className="text-sm leading-relaxed">
									Carpet area varies significantly based on design efficiency. Sellers often lack exact figures, and
									floorplan estimates are imprecise.
									<br />
									We avoid providing specific values to ensure accuracy.
								</p>
							</div>
  }]), {
    label: "Facing",
    value: (supOne.facings || supOne.plotFacings)?.map(facing => toSentenceCase(facing)).join(", ")
  }, {
    label: "Floor No.",
    value: supOne.floors?.filter(Boolean)?.length ? <button className="text-primary-700 underline hover:text-primary-800 cursor-pointer text-sm font-semibold" onClick={() => {
      if (!isAuthenticated) {
        openDialog({
          onLoginSuccess: () => setIsVisitSchedulingOpen(true)
        });
      } else {
        setIsVisitSchedulingOpen(true);
      }
    }}>
					{isVisitRequested ? "Reschedule Visit" : "Request Visit"}
				</button> : null
  }, {
    label: "Parking type",
    value: formatRange({
      data: supOne.parkings,
      formatter: value_2 => value_2.toLocaleString()
    }),
    // suffix: " car (Covered)",
    suffix: " car"
  }, ...(isPlotProperty ? [] : [{
    label: "Possession status",
    value: [...new Set(supOne.possessionStatuses || [])].map(p => toSentenceCase(p)).join(", ")
  }]), {
    label: "Property age",
    value: constructedOn.includes("-") ? "Varies: " + constructedOn : constructedOn,
    info: supOne.type === "unit" && supOne.constructedOnsFromProject?.[0] ? !isSame({
      date1: supOne.constructedOns?.[0],
      date2: supOne.constructedOnsFromProject?.[0]
    }) ? `The block has a different age: ${getAge({
      constructedOn: supOne.constructedOnsFromProject?.[0]
    })}` : "" : ""
  }, {
    label: "Property type",
    value: supOne.propertyTypes?.map(p_0 => toSentenceCase(p_0)).join(", ")
  }, {
    label: "Monthly Rental Value",
    value: formatRange({
      data: supOne.rentalValues,
      formatter: value_3 => `₹${currencify({
        amount: value_3,
        abbreviated: true
      })}`
    })
  }, {
    label: "Monthly Maintenance Cost",
    value: formatRange({
      data: supOne.monthlyMaintenanceCosts,
      formatter: value_4 => `₹${currencify({
        amount: value_4,
        abbreviated: true
      })}`
    })
  }, {
    label: "Servant room",
    value: formatRange({
      data: supOne.servantRooms
    })
  }, {
    label: "Utility",
    value: formatRange({
      data: supOne.utilities
    })
  }, {
    label: "Loan Status",
    value: supOne.loan?.status && supOne.loan?.status !== "none" ? getNameFromCode(loanStatuses, supOne.loan?.status) + "(" + getNameFromCode(banks, supOne.loan?.bank) + ")" : ""
  }].filter(row => Boolean(row.value));

  // Sort detail rows alphabetically by label
  const sortedPropertyRows = [...propertyRows].sort((a, b) => a.label.localeCompare(b.label));
  interface DetailRow {
    label: string;
    value: string | React.ReactNode;
    info?: string | React.ReactNode;
    available?: boolean;
    suffix?: string;
  }
  const renderDetailRow = (row_0: DetailRow, index: number) => <div key={index} className="flex justify-between items-center py-1.5 px-2 border-b border-gray-200">
			<span className="text-sm font-medium text-gray-600">{row_0.label}</span>
			<div className="flex items-center gap-1">
				{row_0.available || !isLoading ? <span className="text-sm font-semibold text-primary-700">
						{row_0.value} {row_0.suffix ?? ""}
					</span> : <SkeletonLoader height="16px" width="30%" />}
				{row_0.info && <Tooltip>
						<TooltipTrigger asChild>
							<Info className="w-3.5 h-3.5 text-primary cursor-help" />
						</TooltipTrigger>
						<TooltipContent>{row_0.info}</TooltipContent>
					</Tooltip>}
			</div>
		</div>;
  return <>
			<div className="bg-white p-2 mt-2">
				<div className="space-y-1">{sortedPropertyRows.map((row_1, index_0) => renderDetailRow(row_1, index_0))}</div>
			</div>

			{/* Visit Scheduling Overlay */}
			<VisitSchedulingOverlay isOpen={isVisitSchedulingOpen} onClose={() => setIsVisitSchedulingOpen(false)} unitId={supOne._id} userId={user?.id} buyerId={user?.groups?.find(group => group.relationship === "buyer")?.id} propertyName={supOne.type === "unit" ? "Unit" : "Project"} onScheduleComplete={handleScheduleComplete} />
		</>;
}